import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  QrCode,
  MessageCircle,
  Star,
  Users,
  TrendingUp,
  Gift,
  Share2,
  BarChart3,
} from "lucide-react";
import { motion } from "framer-motion";

const Features = () => {
  const features = [
    {
      icon: QrCode,
      title: "Magic QR Codes",
      description:
        "Generate smart QR codes that connect customers to reviews, social media, and exclusive offers in one scan.",
      color: "text-primary-600 bg-primary-100",
    },
    {
      icon: MessageCircle,
      title: "SMS Marketing",
      description:
        "Send targeted promotions and updates directly to customers' phones with high engagement rates.",
      color: "text-blue-600 bg-blue-100",
    },
    {
      icon: Star,
      title: "Review Generation",
      description:
        "Automatically prompt satisfied customers to leave reviews on Google, Yelp, and TripAdvisor.",
      color: "text-yellow-600 bg-yellow-100",
    },
    {
      icon: Users,
      title: "Customer Retention",
      description:
        "Build lasting relationships with loyalty programs and personalized communication strategies.",
      color: "text-green-600 bg-green-100",
    },
    {
      icon: Gift,
      title: "Coupon System",
      description:
        "Create and distribute digital coupons that drive repeat visits and increase average order value.",
      color: "text-purple-600 bg-purple-100",
    },
    {
      icon: Share2,
      title: "Social Media Integration",
      description:
        "Connect customers to all your social platforms and grow your online presence organically.",
      color: "text-pink-600 bg-pink-100",
    },
    {
      icon: TrendingUp,
      title: "Revenue Growth",
      description:
        "Increase repeat customers by 40% and boost overall revenue through strategic engagement.",
      color: "text-emerald-600 bg-emerald-100",
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description:
        "Track customer behavior, campaign performance, and ROI with detailed insights and reports.",
      color: "text-indigo-600 bg-indigo-100",
    },
  ];

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to <span className="text-[#9333ea]">Grow</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our comprehensive platform helps businesses increase customer
            retention, generate more reviews, and boost revenue without breaking
            the bank.
          </p>
        </motion.div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: "easeOut",
                }}
              >
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                  <CardHeader className="text-center">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${feature.color} mx-auto mb-4 transition-transform`}
                    >
                      <Icon className="h-8 w-8" />
                    </motion.div>
                    <CardTitle className="text-xl text-gray-900">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-center leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
